import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { revalidateLogic, useForm, useStore } from '@eureca/ui/forms';
import { z } from 'zod';
import {
  JobEditFaqsPage as JobEditFaqsPageComponent,
  type JobFaq,
} from './JobEditFaqsPage.component';
import { FaqDialog, type FaqDialogValue } from './FaqDialog';
import { DefaultFaqsPanel } from '@/shared/default-faqs/DefaultFaqsPanel';
import { useDefaultFaqsPanel } from '@/shared/default-faqs/use-default-faqs-panel';
import { JobEditFaqsLoadingFallback } from './JobEditFaqsLoadingFallback';
import { JobEditFaqsErrorFallback } from './JobEditFaqsErrorFallback';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '@/features/jobs/job-shared/job-status';
import { JobMutationError, usePatchJob } from '@/features/jobs/job-shared/job-edit-mutation';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';

interface JobEditFaqsValues {
  faqs: JobFaq[];
}

const faqShape = z.object({ id: z.string(), question: z.string(), answer: z.string() });
const schema = z.object({ faqs: z.array(faqShape) });

const emptyValues: JobEditFaqsValues = { faqs: [] };

/**
 * Mints a stable client-side ID for a freshly loaded FAQ. Backend doesn't
 * round-trip ids (the JSONB column stores `[{question, answer}]` only), so
 * we generate one here purely for React keys + edit/delete bookkeeping.
 */
function makeFaqId(): string {
  return `faq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

interface PatchPayload {
  faqs: { question: string; answer: string }[];
}

function buildPayload(values: JobEditFaqsValues): PatchPayload {
  return {
    faqs: values.faqs.map((entry) => ({ question: entry.question, answer: entry.answer })),
  };
}

export default function JobEditFaqsPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/perguntas-frequentes',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const patchJob = usePatchJob(jobId);
  const [hasHydrated, setHasHydrated] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [defaultFaqsOpen, setDefaultFaqsOpen] = useState(false);
  // EU-641 — só busca quando o painel abre (FQC-022: loading/erro explícitos
  // sem bloquear o preenchimento das perguntas próprias).
  const defaultFaqsPanel = useDefaultFaqsPanel(
    { kind: 'job', jobId },
    { enabled: defaultFaqsOpen }
  );

  async function persist(values: JobEditFaqsValues) {
    try {
      await patchJob.mutateAsync(buildPayload(values));
      toast('Alterações salvas com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      void navigate({
        to: '/vagas/$jobId',
        params: { jobId },
      });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Vaga não encontrada.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para editar essa vaga.');
      } else {
        toast.error('Não foi possível salvar as alterações. Tente novamente.');
      }
    }
  }

  const form = useForm({
    defaultValues: emptyValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      await persist(value);
    },
  });

  const job = jobQuery.data;
  // EU-569 — vaga Encerrada/Cancelada: seção em modo somente visualização.
  const readOnly = isJobDetailReadOnly(job);
  const jobName = job?.name ?? '';
  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  // Hydrate from job.faqs once. Mint client-side IDs so React keys are
  // stable in this session; they're regenerated next time the page mounts.
  useEffect(() => {
    if (hasHydrated || !job) return;
    const hydrated: JobFaq[] = (job.faqs ?? []).map((entry) => ({
      id: makeFaqId(),
      question: entry.question,
      answer: entry.answer,
    }));
    form.setFieldValue('faqs', hydrated);
    setHasHydrated(true);
  }, [job, hasHydrated, form]);

  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  function handleAddFaq() {
    setEditingId(null);
    setFaqDialogOpen(true);
  }

  function handleEditFaq(faqId: string) {
    setEditingId(faqId);
    setFaqDialogOpen(true);
  }

  function handleDeleteFaq(faqId: string) {
    const next = values.faqs.filter((entry) => entry.id !== faqId);
    form.setFieldValue('faqs', next);
    toast('Pergunta excluída com sucesso', {
      action: { label: 'Fechar', onClick: () => undefined },
    });
  }

  function handleFaqSubmit(value: FaqDialogValue) {
    if (editingId) {
      const next = values.faqs.map((entry) =>
        entry.id === editingId
          ? { ...entry, question: value.question, answer: value.answer }
          : entry
      );
      form.setFieldValue('faqs', next);
      setFaqDialogOpen(false);
      toast('Pergunta atualizada com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      return;
    }
    const next = [
      ...values.faqs,
      { id: makeFaqId(), question: value.question, answer: value.answer },
    ];
    form.setFieldValue('faqs', next);
    setFaqDialogOpen(false);
    toast('Pergunta adicionada com sucesso', {
      action: { label: 'Fechar', onClick: () => undefined },
    });
  }

  function handleBack() {
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  // Loading state.
  if (jobQuery.isLoading) {
    return <JobEditFaqsLoadingFallback />;
  }

  // Erro de carregamento (≠ 404).
  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status !== 404) {
      return <JobEditFaqsErrorFallback onRetry={() => void jobQuery.refetch()} />;
    }
  }

  const editingFaq = editingId
    ? (values.faqs.find((entry) => entry.id === editingId) ?? null)
    : null;

  return (
    <>
      <JobEditFaqsPageComponent
        jobName={jobName}
        companyLogoUrl={companyLogoUrl}
        companyName={companyName}
        faqs={values.faqs}
        isSubmitting={isSubmitting || patchJob.isPending}
        readOnly={readOnly}
        onAddFaq={handleAddFaq}
        onEditFaq={handleEditFaq}
        onDeleteFaq={handleDeleteFaq}
        onOpenDefaultFaqs={() => setDefaultFaqsOpen(true)}
        onBack={handleBack}
        onSubmit={() => {
          // EU-569 — modo visualização: nada persiste.
          if (readOnly) return;
          void form.handleSubmit();
        }}
      />
      <FaqDialog
        open={faqDialogOpen}
        onOpenChange={(open) => {
          setFaqDialogOpen(open);
          if (!open) setEditingId(null);
        }}
        initialValue={
          editingFaq ? { question: editingFaq.question, answer: editingFaq.answer } : null
        }
        onSubmit={handleFaqSubmit}
      />
      {/* EU-641 — painel com switches de visibilidade; persistência imediata
          por alternância (registro já existe na criação E na edição da vaga).
          Vaga terminal → switches desabilitados (FQC-023). */}
      <DefaultFaqsPanel
        open={defaultFaqsOpen}
        onOpenChange={setDefaultFaqsOpen}
        items={defaultFaqsPanel.items}
        loading={defaultFaqsPanel.loading}
        error={defaultFaqsPanel.error}
        onRetry={defaultFaqsPanel.refetch}
        disabled={readOnly}
        onToggle={(faqId, hidden) => {
          if (readOnly) return;
          defaultFaqsPanel.toggle(faqId, hidden);
        }}
      />
    </>
  );
}
